import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';
import { Icon } from '@openedx/paragon';
import { Nightlight, WbSunny } from '@openedx/paragon/icons';
import { useIntl } from '@edx/frontend-platform/i18n';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}
let themeVariant = 'selected-paragon-theme-variant';

const AddDarkTheme = () => {
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body {
            background-color: #0D0D0E;
            color: #ccc;
          }
          a { color: #ccc; }
          a:hover { color: #d3d3d3; }
        `;
        if (iframes[index].contentDocument) {
          iframes[index].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  useEffect(() => {
    const theme = window.localStorage.getItem(themeVariant);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
const IndigoFooter = () => {
  const intl = useIntl();
  const config = getConfig();

  const indigoFooterNavLinks = config.INDIGO_FOOTER_NAV_LINKS || [];

  const messages = {
    "footer.poweredby.text": {
      id: "footer.poweredby.text",
      defaultMessage: "Powered by",
      description: "text for the footer",
    },
    "footer.tutorlogo.altText": {
      id: "footer.tutorlogo.altText",
      defaultMessage: "Runs on Tutor",
      description: "alt text for the footer tutor logo",
    },
    "footer.logo.altText": {
      id: "footer.logo.altText",
      defaultMessage: "Powered by Open edX",
      description: "alt text for the footer logo.",
    },
    "footer.copyright.text": {
      id: "footer.copyright.text",
      defaultMessage: `Copyrights ©${new Date().getFullYear()}. All Rights Reserved.`,
      description: "copyright text for the footer",
    },
  };

  return (
    <div className="wrapper wrapper-footer">
      <footer id="footer" className="tutor-container">
        <div className="footer-top">
          <div className="powered-area">
            <ul className="logo-list">
              <li>{intl.formatMessage(messages["footer.poweredby.text"])}</li>
              <li>
                <a
                  href="https://edly.io/tutor/"
                  rel="noreferrer"
                  target="_blank"
                >
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/tutor-logo.png`}
                    alt={intl.formatMessage(
                      messages["footer.tutorlogo.altText"]
                    )}
                    width="57"
                  />
                </a>
              </li>
              <li>
                <a href="https://open.edx.org" rel="noreferrer" target="_blank">
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/openedx-logo.png`}
                    alt={intl.formatMessage(messages["footer.logo.altText"])}
                    width="79"
                  />
                </a>
              </li>
            </ul>
          </div>
          <nav className="nav-colophon">
            <ol>
              {indigoFooterNavLinks.map((link) => (
                <li key={link.url}>
                  <a href={`${link.url.startsWith("http") ? link.url : config.LMS_BASE_URL + link.url}`}>{link.title}</a>
                </li>
              ))}
            </ol>
          </nav>
        </div>
        <span className="copyright-site">
          {intl.formatMessage(messages["footer.copyright.text"])}
        </span>
      </footer>
    </div>
  );
};
const MobileViewHeader = () => {
  const config = getConfig();
  const intl = useIntl();
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "My Open edX",
      description: "alt text for the mobile view header logo",
    },
  };

  const BASE_URL = config.LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/indigo/images/logo.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
      </a>
    </>
  );
};
const ThemedLogo = () => {
  const BASE_URL = getConfig().LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/indigo/images/logo.png`} alt="Open edX" />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt="Open edX" />
      </a>
    </>
  );
};
const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(false);

  const themeCookie = 'selected-paragon-theme-variant';
  const themeCookieExpiry = 90; // days
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookie = (name) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  };

  const setCookie = (name, value, { domain, path, expires }) => {
    document.cookie = `${name}=${value}; domain=${domain}; path=${path}; expires=${expires.toUTCString()}; SameSite=Lax`;
  };

  const serverURL = new URL(getConfig().LMS_BASE_URL);

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + themeCookieExpiry
    );
  };

  const getCookieOptions = (serverURL) => ({
    domain: serverURL.hostname,
    path: '/',
    expires: getCookieExpiry(),
  });

  const onToggleTheme = () => {
    let theme = '';

    if (getCookie(themeCookie) === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'light');
      setIsDarkThemeEnabled(false);
      theme = 'light';
    } else {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');
      setIsDarkThemeEnabled(true);
      theme = 'dark';
    }

    window.localStorage.setItem(themeCookie, theme);
    setTimeout(() => {
      setCookie(themeCookie, theme, getCookieOptions(serverURL));
      window.location.reload();
    }, 1);
  };

  useEffect(() => {
    if (!getCookie(themeCookie) || getCookie(themeCookie) === 'undefined') {
      return;
    }
    if (getCookie(themeCookie) !== window.localStorage.getItem(themeCookie)) {
      window.localStorage.setItem(themeCookie, getCookie(themeCookie));
      window.location.reload();
    }
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Toggle Theme",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            defaultChecked={getCookie(themeCookie) === "dark"}
            onChange={onToggleTheme}
            onKeyUp={handleKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${isDarkThemeEnabled ? "Light" : "Dark"
            } Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};

    const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
const GeekGuruMark = ({ compact = false }) => (
  <span className={`gg-academy-mark${compact ? ' gg-academy-mark--compact' : ''}`} aria-hidden="true">
    <svg viewBox="0 0 48 48" width={compact ? 26 : 34} height={compact ? 26 : 34} focusable="false">
      <path className="gg-academy-mark__frame" d="M24 3.5 41.5 13.6v20.8L24 44.5 6.5 34.4V13.6Z" />
      <path className="gg-academy-mark__accent" d="M24 8.8 36.9 16v15.9L24 39.2 11.1 32V16Z" />
      <text x="24" y="29.5" textAnchor="middle">GG</text>
      <circle cx="38.5" cy="9.5" r="3.1" />
    </svg>
  </span>
);

// `public/index.html` uses a static `lang="en-us"` in several upstream MFEs.
// Expose the locale selected by frontend-platform/IntlProvider instead of
// inferring it from that bootstrap document or from client-side storage.
const GeekGuruLocaleBridge = () => {
  const intl = useIntl();
  return (
    <span
      hidden
      aria-hidden="true"
      data-geekguru-intl-locale={`${intl.locale || ''}`}
    />
  );
};

const getGeekGuruShellCopy = (intl, config) => {
  const locale = `${intl.locale || config.LANGUAGE_CODE || config.GEEKGURU_DEFAULT_LANGUAGE || 'fr'}`
    .toLowerCase()
    .split(/[-_]/)[0];
  return locale === 'en'
    ? {
      academyHome: 'Geek Guru Academy home',
      website: 'Geek Guru website',
      specialties: 'Cloud · DevOps · AI',
      promise: 'Learn · Practice · Prove',
      footerNavigation: 'Academy navigation',
      catalog: 'Catalog',
      help: 'Help',
      accessibility: 'Accessibility',
      privacy: 'Privacy',
      terms: 'Terms',
      contact: 'Contact',
      mainWebsite: 'Main website',
      reach: 'FR/EN courses from Cameroon, available worldwide.',
    }
    : {
      academyHome: 'Accueil Geek Guru Academy',
      website: 'Site Geek Guru',
      specialties: 'Cloud · DevOps · IA',
      promise: 'Apprendre · Pratiquer · Prouver',
      footerNavigation: 'Navigation de l’Academy',
      catalog: 'Catalogue',
      help: 'Aide',
      accessibility: 'Accessibilité',
      privacy: 'Confidentialité',
      terms: 'Conditions',
      contact: 'Contact',
      mainWebsite: 'Site principal',
      reach: 'Formations FR/EN depuis le Cameroun, accessibles partout.',
    };
};

const GeekGuruLogo = () => {
  const intl = useIntl();
  const config = getConfig();
  const copy = getGeekGuruShellCopy(intl, config);
  const dashboardUrl = `${config.GEEKGURU_LEARNER_DASHBOARD_URL || config.LEARNER_DASHBOARD_MFE_BASE_URL || '/learner-dashboard'}`.replace(/\/$/, '');
  const siteUrl = config.GEEKGURU_SITE_URL || config.LMS_BASE_URL || '/';
  return (
    <div className="gg-academy-brand" data-geekguru-mfe="3.1.0">
      <GeekGuruLocaleBridge />
      <a className="gg-academy-brand__home" href={`${dashboardUrl}/`} aria-label={copy.academyHome}>
        <GeekGuruMark />
        <span className="gg-academy-brand__text">
          <strong>Geek Guru Academy</strong>
          <small>{copy.specialties}</small>
        </span>
      </a>
      <a className="gg-academy-brand__site" href={siteUrl} target="_blank" rel="noreferrer">
        {copy.website} <span aria-hidden="true">↗</span>
      </a>
    </div>
  );
};

const GeekGuruMobileLogo = () => {
  const intl = useIntl();
  const config = getConfig();
  const copy = getGeekGuruShellCopy(intl, config);
  const dashboardUrl = `${config.GEEKGURU_LEARNER_DASHBOARD_URL || config.LEARNER_DASHBOARD_MFE_BASE_URL || '/learner-dashboard'}`.replace(/\/$/, '');
  return (
    <a className="gg-academy-mobile-brand" href={`${dashboardUrl}/`} aria-label={copy.academyHome}>
      <GeekGuruLocaleBridge />
      <GeekGuruMark compact />
      <span>Geek Guru Academy</span>
    </a>
  );
};

const GeekGuruFooter = () => {
  const intl = useIntl();
  const config = getConfig();
  const copy = getGeekGuruShellCopy(intl, config);
  const academyUrl = config.GEEKGURU_ACADEMY_URL || config.LMS_BASE_URL || '/';
  const siteUrl = config.GEEKGURU_SITE_URL || academyUrl;
  const catalogUrl = config.GEEKGURU_CATALOG_URL || config.GEEKGURU_TRAINING_URL || `${academyUrl}/courses`;
  const contactUrl = config.GEEKGURU_CONTACT_URL || `${siteUrl}/contact/`;
  const privacyUrl = config.PRIVACY_POLICY_URL || `${siteUrl}/politique-de-confidentialite/`;
  const termsUrl = config.TERMS_OF_SERVICE_URL || `${siteUrl}/mentions-legales/`;
  const accessibilityUrl = config.ACCESSIBILITY_PAGE_URL || `${siteUrl}/accessibilite/`;
  const accessibilityEnabled = config.ENABLE_ACCESSIBILITY_PAGE === true;
  const supportEmail = config.SUPPORT_EMAIL || config.CONTACT_EMAIL || '';
  return (
    <footer className="gg-academy-footer" data-geekguru-mfe-footer="3.1.0">
      <GeekGuruLocaleBridge />
      <div className="gg-academy-footer__inner">
        <div className="gg-academy-footer__brand">
          <GeekGuruMark compact />
          <span>
            <strong>Geek Guru Academy</strong>
            <small>{copy.promise}</small>
          </span>
        </div>
        <nav aria-label={copy.footerNavigation}>
          <a href={catalogUrl}>{copy.catalog}</a>
          {supportEmail && <a href={`mailto:${supportEmail}`}>{copy.help}</a>}
          {accessibilityEnabled && <a href={accessibilityUrl}>{copy.accessibility}</a>}
          <a href={privacyUrl}>{copy.privacy}</a>
          <a href={termsUrl}>{copy.terms}</a>
          <a href={contactUrl}>{copy.contact}</a>
          <a href={siteUrl}>{copy.mainWebsite} <span aria-hidden="true">↗</span></a>
        </nav>
      </div>
      <div className="gg-academy-footer__meta">
        <span>© {new Date().getFullYear()} Geek Guru</span>
        <span>{copy.reach}</span>
      </div>
    </footer>
  );
};

// Replace the Catalog MFE starter copy through its public overlay slot. This
// keeps the Academy wording independent from upstream English defaults while
// still following the locale selected by the MFE.
const GeekGuruCatalogHero = () => {
  const intl = useIntl();
  const config = getConfig();
  const locale = `${intl.locale || config.LANGUAGE_CODE || config.GEEKGURU_DEFAULT_LANGUAGE || 'fr'}`
    .toLowerCase()
    .split(/[-_]/)[0];
  const copy = locale === 'en'
    ? {
      eyebrow: 'Geek Guru Academy course catalog',
      title: 'Find the course that moves your skills forward.',
      subtitle: 'Cloud, DevOps and AI learning paths in French and English, with guided practice, labs and Azure certification preparation.',
    }
    : {
      eyebrow: 'Catalogue Geek Guru Academy',
      title: 'Trouvez la formation qui fera progresser vos compétences.',
      subtitle: 'Des parcours Cloud, DevOps et IA en français et en anglais, avec pratique guidée, labs et préparation aux certifications Azure.',
    };

  return (
    <div className="gg-catalog-hero" data-geekguru-catalog-hero="3.1.0">
      <GeekGuruLocaleBridge />
      <span className="gg-catalog-hero__eyebrow">{copy.eyebrow}</span>
      <h1 className="display-1 text-center gg-catalog-hero__title">{copy.title}</h1>
      <p className="lead text-center mb-3 gg-catalog-hero__subtitle">{copy.subtitle}</p>
    </div>
  );
};
    addPlugins(config, 'org.openedx.frontend.layout.header_desktop_secondary_menu.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    addPlugins(config, 'org.openedx.frontend.layout.studio_header_search_button_slot.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    if (process.env.APP_ID == 'admin-console') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'authn') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'authoring') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'org.openedx.frontend.layout.studio_header_search_button_slot.v1', [
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                priority: 10,
                id: 'custom_notification_tray_before',
                type: DIRECT_PLUGIN,
                RenderWidget: ToggleThemeButton,
            },
        },
        ]);
      addPlugins(config, 'org.openedx.frontend.layout.studio_footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'account') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'communications') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'discussions') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'gradebook') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'learner-dashboard') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'learning') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        }
        ]);
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'theme_switch_button',
                type: DIRECT_PLUGIN,
                RenderWidget: ToggleThemeButton,
            },
        },
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'ora-grading') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'profile') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'catalog') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
if (config.pluginSlots && config.pluginSlots['logo_slot']) {
  config.pluginSlots['logo_slot'].keepDefault = false;

  const logoInsertPlugins =
    config.pluginSlots['logo_slot'].plugins.filter(plugin => plugin.op === 'insert' && plugin.widget?.id === 'custom_logo');

  if (logoInsertPlugins.length > 1) {
    let kept = false;
    config.pluginSlots['logo_slot'].plugins =
      config.pluginSlots['logo_slot'].plugins.filter(plugin => {
        if (plugin.op === 'insert' && plugin.widget?.id === 'custom_logo') {
          if (kept) return false;
          kept = true;
        }
        return true;
      });
  }
}

if (config.pluginSlots && config.pluginSlots['org.openedx.frontend.layout.footer.v1']) {
  const footerPluginsToInsert =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => plugin.op === 'insert');

  if (footerPluginsToInsert.length > 2) {
  config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => {
      if (plugin.op === 'insert') {
        const widgetId = plugin.widget?.id;
        return widgetId !== 'indigo_footer';
      }
      return true;
    });
  }
}
// Paragon theme loading differs slightly between MFEs. Load the Academy shell
// explicitly so the custom logo/footer never render as unstyled raw markup.
const geekGuruRuntimeConfig = getConfig();
const geekGuruAssetBase = (
  geekGuruRuntimeConfig.LMS_BASE_URL
  || geekGuruRuntimeConfig.GEEKGURU_ACADEMY_URL
  || `${geekGuruRuntimeConfig.LOGO_URL || ''}`.split('/static/')[0]
  || ''
).replace(/\/$/, '');
const geekGuruAssetVersion = geekGuruRuntimeConfig.GEEKGURU_ASSET_TAG || 'v310';
const normalizeGeekGuruLanguage = value => `${value || ''}`.trim().toLowerCase().split(/[-_]/)[0];
const geekGuruIntlLocaleSelector = '[data-geekguru-intl-locale]';
let geekGuruLanguage = '';
const syncGeekGuruEffectiveLanguage = (root = document) => {
  if (typeof document === 'undefined' || !root) return false;
  const localeMarker = root.matches?.(geekGuruIntlLocaleSelector)
    ? root
    : root.querySelector?.(geekGuruIntlLocaleSelector)
      || document.querySelector(geekGuruIntlLocaleSelector);
  const nextLanguage = normalizeGeekGuruLanguage(
    localeMarker?.getAttribute('data-geekguru-intl-locale'),
  );
  if (!nextLanguage) return false;
  const changed = nextLanguage !== geekGuruLanguage;
  geekGuruLanguage = nextLanguage;
  document.documentElement.lang = nextLanguage;
  document.documentElement.dataset.geekguruIntlLocale = nextLanguage;
  return changed;
};
const geekGuruAppId = `${process.env.APP_ID || geekGuruRuntimeConfig.APP_ID || ''}`.trim().toLowerCase();

// The Ulmo translation catalogue does not yet cover every string emitted by
// the Learning, Learner Dashboard, Catalog and Authoring MFEs. Keep this small, reviewed
// runtime catalogue until the same messages are available from Atlas. Each
// message is context-limited below so course-authored content is never
// translated by this compatibility layer.
const geekGuruFrenchUiMessages = Object.freeze({
  'Skip to content': 'Aller au contenu',
  'Skip to main content': 'Aller au contenu principal',
  'Skip to main content.': 'Aller au contenu principal',
  'Main content': 'Contenu principal',
  'Main': 'Navigation principale',
  'Secondary': 'Navigation secondaire',
  'Main Menu': 'Menu principal',
  'Account Menu': 'Menu du compte',
  'Notification bell icon': 'Notifications',
  'Course': 'Cours',
  'Progress': 'Progression',
  'Dates': 'Dates',
  'Take the course!': 'Découvrir le cours',
  'You can explore sections of the course using the outline below.': 'Explorez les sections du cours à partir du plan ci-dessous.',
  'Pick up where you left off': 'Reprendre là où vous vous êtes arrêté',
  'Resume course': 'Reprendre le cours',
  'Dismiss': 'Fermer',
  'Next': 'Suivant',
  'Expand all': 'Tout développer',
  'Additional course resources': 'Ressources complémentaires du cours',
  'These tabs can be used to access other course materials, such as your progress, syllabus, etc.': 'Ces onglets donnent accès aux autres ressources du cours, notamment votre progression et le programme.',
  'Okay': 'Compris',
  'Dashboard': 'Tableau de bord',
  'Profile': 'Profil',
  'Order History': 'Historique des commandes',
  'Sign Out': 'Se déconnecter',
  'Enroll now': "S'inscrire",
  'Course Tools': 'Outils du cours',
  'View this course as:': 'Voir ce cours en tant que :',
  'Staff': 'Équipe pédagogique',
  'View course in:': 'Afficher le cours en mode :',
  'Instructor': 'Formateur',
  'Bookmarks': 'Favoris',
  'Launch tour': 'Lancer la visite guidée',
  'More...': 'Plus…',
  'More…': 'Plus…',
  'Course Material': 'Contenu du cours',
  'User Options': 'Options utilisateur',
  'Completed section': 'Section terminée',
  'Incomplete section': 'Section non terminée',
  'Completed': 'Terminé',
  'Close': 'Fermer',
  'Open': 'Ouvrir',
  'Search for a course': 'Rechercher une formation',
  'Search': 'Rechercher',
  'search': 'rechercher',
  'submit search': 'lancer la recherche',
  'View all courses': 'Voir toutes les formations',
  'Discover new': 'Découvrir',
  'Help': 'Aide',
  'Explore courses': 'Explorer les formations',
  'Filters': 'Filtres',
  'Languages': 'Langues',
  'Course types': 'Types de formation',
  'Organizations': 'Organismes',
  'Audit': 'Accès libre',
  'Honor': 'Attestation',
  'table pagination': 'Pagination des formations',
  'Starts:': 'Début :',
  'You are enrolled in this course': 'Vous êtes inscrit à ce cours',
  'View course': 'Accéder au cours',
  'View About Page in Studio': 'Voir la page de présentation dans Studio',
  'Account': 'Compte',
  'Logout': 'Se déconnecter',
  'Course number': 'Numéro du cours',
  'Classes start': 'Début du cours',
  'Estimated effort': 'Charge de travail estimée',
  'Price': 'Prix',
  'Free': 'Gratuit',
  'Social sharing options': 'Options de partage sur les réseaux sociaux',
  "Tweet that you've enrolled in this course": 'Annoncer votre inscription sur X',
  "Post a Facebook message to say you've enrolled in this course": 'Annoncer votre inscription sur Facebook',
  "Email someone to say you've enrolled in this course": 'Annoncer votre inscription par e-mail',
  'Powered by Open edX': 'Propulsé par Open edX',
  'Studio home': 'Accueil Studio',
  'New course': 'Nouveau cours',
  'New library': 'Nouvelle bibliothèque',
  'Courses': 'Cours',
  'Libraries': 'Bibliothèques',
  'Beta': 'Bêta',
  'Legacy Libraries': 'Bibliothèques classiques',
  'Taxonomies': 'Taxonomies',
  'All courses': 'Tous les cours',
  'Name A-Z': 'Nom A-Z',
  'Previous': 'Précédent',
  'Clear filters': 'Effacer les filtres',
  'New to Geek Guru Academy - Studio?': 'Vous découvrez Geek Guru Academy – Studio ?',
  'Click "Looking for help with Studio" at the bottom of the page to access our continually updated documentation and other Studio resources.': 'Cliquez sur « Besoin d’aide avec Studio ? » en bas de la page pour accéder à notre documentation régulièrement mise à jour et aux autres ressources Studio.',
  'Terms of Service': "Conditions d'utilisation",
  'Privacy Policy': 'Politique de confidentialité',
  'LMS': 'Espace apprenant',
  'edX Inc': 'edX Inc'
});

const geekGuruFrenchUiContexts = Object.freeze({
  'Skip to content': 'a[href="#main"], a[href="#content"], [class*="skip"]',
  'Skip to main content': 'a[href="#main"], a[href="#main-content"], [class*="skip"]',
  'Skip to main content.': 'a[href="#main"], a[href="#main-content"], [class*="skip"]',
  'Main content': 'main, [role="main"], [aria-label]',
  'Main': 'header, nav, [role="banner"], [aria-label]',
  'Secondary': 'header, nav, [role="banner"], [aria-label]',
  'Main Menu': 'header, button, [role="banner"]',
  'Account Menu': 'header, button, [role="banner"]',
  'Notification bell icon': 'header, button, [role="banner"]',
  'Course': '[role="tab"], [role="tablist"], nav, .nav, .tabs',
  'Progress': '[role="tab"], [role="tablist"], nav, .nav, .tabs',
  'Dates': '[role="tab"], [role="tablist"], nav, .nav, .tabs',
  'Take the course!': '[role="dialog"], [role="tooltip"], [class*="tour"], [class*="coach"], [class*="popover"], h1, h2, h3, h4, [role="heading"]',
  'You can explore sections of the course using the outline below.': '[role="dialog"], [role="tooltip"], [class*="tour"], [class*="coach"], [class*="popover"]',
  'Pick up where you left off': 'h1, h2, h3, h4, [role="heading"], [data-testid="start-resume-card"], .pgn__card-header-title-md',
  'Resume course': 'a, button, [role="button"], [role="dialog"], [role="tooltip"]',
  'Dismiss': 'a, button, [role="button"], [role="dialog"], [role="tooltip"]',
  'Next': 'a, button, [role="button"], [role="dialog"], [role="tooltip"]',
  'Expand all': 'a, button, [role="button"]',
  'Additional course resources': '[role="dialog"], [role="tooltip"], [class*="tour"], [class*="coach"], [class*="popover"], h1, h2, h3, h4, [role="heading"]',
  'These tabs can be used to access other course materials, such as your progress, syllabus, etc.': '[role="dialog"], [role="tooltip"], [class*="tour"], [class*="coach"], [class*="popover"]',
  'Okay': 'a, button, [role="button"], [role="dialog"], [role="tooltip"]',
  'Dashboard': 'a, button, [role="menuitem"], [role="menu"], .dropdown-menu',
  'Profile': 'a, button, [role="menuitem"], [role="menu"], .dropdown-menu',
  'Order History': 'a, button, [role="menuitem"], [role="menu"], .dropdown-menu',
  'Sign Out': 'a, button, [role="menuitem"], [role="menu"], .dropdown-menu',
  'Enroll now': 'a, button, [role="button"], .course-card',
  'Course Tools': 'h1, h2, h3, h4, [role="heading"], nav',
  'View this course as:': 'div, label, legend, form, [role="group"]',
  'Staff': 'a, button, option, select, [role="button"], [role="option"], [role="combobox"]',
  'View course in:': 'div, label, legend, form, [role="group"]',
  'Instructor': 'a, button, option, select, [role="button"], [role="option"], [role="combobox"]',
  'Bookmarks': 'a, button, [role="button"], nav',
  'Launch tour': 'a, button, [role="button"]',
  'More...': 'a, button, nav, [role="tablist"]',
  'More…': 'a, button, nav, [role="tablist"]',
  'Course Material': 'nav, [role="navigation"], [aria-label]',
  'User Options': 'header, button, [role="banner"]',
  'Completed section': '[aria-label], [role="status"], [class*="status"], [class*="section"], .course-outline-tab .sr-only',
  'Incomplete section': '[aria-label], [role="status"], [class*="status"], [class*="section"], .course-outline-tab .sr-only',
  'Completed': '[aria-label], [role="status"], [class*="status"], [class*="progress"], .course-outline-tab .sr-only',
  'Close': 'a, button, [role="button"], [role="dialog"], [role="tooltip"]',
  'Open': 'a, button, [role="button"], [aria-expanded]',
  'Search for a course': 'input, label, form, [role="search"]',
  'Search': 'a, button, input, label, form, [role="search"]',
  'search': 'a, button, input, label, form, [role="search"]',
  'submit search': 'a, button, input, label, form, [role="search"]',
  'View all courses': 'a, button, [role="button"]',
  'Discover new': 'header, nav, a',
  'Help': 'header, nav, a',
  'Explore courses': 'main, h1, h2, [role="heading"]',
  'Filters': 'main, aside, h1, h2, h3, [role="heading"]',
  'Languages': 'main, aside, [role="group"], p',
  'Course types': 'main, aside, [role="group"], p',
  'Organizations': 'main, aside, [role="group"], p',
  'Audit': 'main, aside, [role="group"], label',
  'Honor': 'main, aside, [role="group"], label',
  'table pagination': 'main, nav, [role="navigation"], [aria-label]',
  'Starts:': '.course-card, [class*="course-card"], [class*="course"]',
  'You are enrolled in this course': '[role="status"], [class*="alert"], [class*="enroll"], main',
  'View course': 'a, button, [role="button"]',
  'View About Page in Studio': 'a, button, [role="button"]',
  'Account': 'a, button, [role="menuitem"], [role="menu"], .dropdown-menu',
  'Logout': 'a, button, [role="menuitem"], [role="menu"], .dropdown-menu',
  'Course number': 'dt, th, aside, [class*="sidebar"], [class*="label"]',
  'Classes start': 'dt, th, aside, [class*="sidebar"], [class*="label"]',
  'Estimated effort': 'dt, th, aside, [class*="sidebar"], [class*="label"]',
  'Price': 'dt, th, aside, [class*="sidebar"], [class*="label"]',
  'Free': 'dd, td, aside, .course-card, [class*="sidebar"], [class*="price"]',
  'Social sharing options': 'nav, aside, [role="group"], [class*="social"], [class*="share"]',
  "Tweet that you've enrolled in this course": 'a, button, [role="button"], [class*="social"], [class*="share"]',
  "Post a Facebook message to say you've enrolled in this course": 'a, button, [role="button"], [class*="social"], [class*="share"]',
  "Email someone to say you've enrolled in this course": 'a, button, [role="button"], [class*="social"], [class*="share"]',
  'Powered by Open edX': 'a, footer, [role="contentinfo"]',
  'Studio home': 'a, button, nav, header, h1, h2, [role="heading"], [role="navigation"]',
  'New course': 'a, button, [role="button"]',
  'New library': 'a, button, [role="button"]',
  'Courses': '[role="tab"], [role="tablist"], nav, h1, h2, [role="heading"]',
  'Libraries': '[role="tab"], [role="tablist"], nav, h1, h2, [role="heading"]',
  'Beta': '[class*="badge"], [class*="pill"], [role="status"], nav',
  'Legacy Libraries': 'a, button, nav, [role="navigation"]',
  'Taxonomies': 'a, button, nav, [role="navigation"]',
  'All courses': 'option, select, [role="option"], [role="combobox"]',
  'Name A-Z': 'option, select, [role="option"], [role="combobox"]',
  'Previous': 'a, button, [role="button"], [class*="pagination"]',
  'Clear filters': 'a, button, [role="button"]',
  'New to Geek Guru Academy - Studio?': 'aside, [class*="help"], h1, h2, h3, h4, [role="heading"]',
  'Click "Looking for help with Studio" at the bottom of the page to access our continually updated documentation and other Studio resources.': 'aside, [class*="help"], [class*="support"], main',
  'Terms of Service': 'a, footer, [role="contentinfo"]',
  'Privacy Policy': 'a, footer, [role="contentinfo"]',
  'LMS': 'a, footer, [role="contentinfo"]',
  'edX Inc': 'a, footer, [role="contentinfo"]'
});

const geekGuruFrenchCommonUiMessages = Object.freeze([
  'Skip to content',
  'Skip to main content',
  'Skip to main content.',
  'Main content',
  'Main',
  'Secondary',
  'Main Menu',
  'Account Menu',
  'Notification bell icon',
  'Dashboard',
  'Profile',
  'Account',
  'Order History',
  'Sign Out',
  'Logout',
  'Close',
  'Open',
  'Terms of Service',
  'Privacy Policy',
  'LMS',
  'edX Inc',
  'Powered by Open edX'
]);

const geekGuruFrenchUiMessagesByApp = Object.freeze({
  learning: Object.freeze([
    'Course',
    'Progress',
    'Dates',
    'Take the course!',
    'You can explore sections of the course using the outline below.',
    'Pick up where you left off',
    'Resume course',
    'Dismiss',
    'Next',
    'Expand all',
    'Additional course resources',
    'These tabs can be used to access other course materials, such as your progress, syllabus, etc.',
    'Okay',
    'Course Tools',
    'View this course as:',
    'Staff',
    'View course in:',
    'Instructor',
    'Bookmarks',
    'Launch tour',
    'More...',
    'More…',
    'Course Material',
    'User Options',
    'Completed section',
    'Incomplete section',
    'Completed'
  ]),
  'learner-dashboard': Object.freeze([
    'Resume course'
  ]),
  catalog: Object.freeze([
    'Enroll now',
    'Search for a course',
    'Search',
    'search',
    'submit search',
    'View all courses',
    'Courses',
    'Discover new',
    'Help',
    'Explore courses',
    'Filters',
    'Languages',
    'Course types',
    'Organizations',
    'Audit',
    'Honor',
    'table pagination',
    'Previous',
    'Next',
    'Starts:',
    'You are enrolled in this course',
    'View course',
    'View About Page in Studio',
    'Course number',
    'Classes start',
    'Estimated effort',
    'Price',
    'Free',
    'Social sharing options',
    "Tweet that you've enrolled in this course",
    "Post a Facebook message to say you've enrolled in this course",
    "Email someone to say you've enrolled in this course"
  ]),
  authoring: Object.freeze([
    'Search',
    'search',
    'submit search',
    'Studio home',
    'New course',
    'New library',
    'Courses',
    'Libraries',
    'Beta',
    'Legacy Libraries',
    'Taxonomies',
    'All courses',
    'Name A-Z',
    'More...',
    'More…',
    'Previous',
    'Next',
    'Clear filters',
    'New to Geek Guru Academy - Studio?',
    'Click "Looking for help with Studio" at the bottom of the page to access our continually updated documentation and other Studio resources.'
  ])
});

const geekGuruChromeUiSelectors = Object.freeze({
  learning: 'header, footer, main, nav, aside, [data-testid="instructor-toolbar"], [data-testid="start-resume-card"], [role="dialog"], [role="tooltip"], [role="menu"], [role="tablist"], [role="group"], [role="status"], [class*="learning"], [class*="course"], [class*="outline"], [class*="progress"], [class*="tour"], [class*="coach"], [class*="popover"], [class*="skip"], a[href="#main-content"]',
  'learner-dashboard': 'header, footer, nav, aside, [role="dialog"], [role="menu"], [role="status"], .course-card, [class*="dashboard"], [class*="skip"], a[href="#main"]',
  catalog: 'header, footer, main, nav, aside, [role="dialog"], [role="menu"], [role="search"], [role="status"], .course-card, [class*="skip"], a[href="#main"]',
  authoring: 'header, footer, main, nav, aside, .studio-header, [role="dialog"], [role="menu"], [role="search"], [role="status"], [class*="skip"], a[href="#main"], a[href="#content"]'
});

const geekGuruExcludedUiSelector = [
  'iframe',
  '[translate="no"]',
  '.notranslate',
  '.xblock',
  '[class*="xblock-"]',
  '[data-usage-id]',
  '[data-block-type]',
  '.courseware',
  '[class*="courseware"]',
  '.unit-container',
  '[class*="unit-container"]'
].join(', ');

const geekGuruSupportedMfeApps = Object.freeze(Object.keys(geekGuruFrenchUiMessagesByApp));
const geekGuruMessageAllowedForApp = source => (
  geekGuruFrenchCommonUiMessages.includes(source)
  || (geekGuruFrenchUiMessagesByApp[geekGuruAppId] || []).includes(source)
);
const geekGuruIsExcludedUi = element => Boolean(element?.closest?.(geekGuruExcludedUiSelector));
const geekGuruIsChromeUi = element => {
  const selector = geekGuruChromeUiSelectors[geekGuruAppId];
  return Boolean(selector && element?.closest?.(selector) && !geekGuruIsExcludedUi(element));
};

const geekGuruFrenchUiPatterns = Object.freeze([
  {
    pattern: /^Showing\s+(\d+)\s+of\s+(\d+)$/,
    replacement: 'Affichage de $1 sur $2',
    selector: '[role="status"], [aria-live], [class*="pagination"], [class*="results"]',
    apps: ['authoring']
  },
  {
    pattern: /^Showing\s+(\d+)\s*-\s*(\d+)\s+of\s+(\d+)\.$/,
    replacement: 'Affichage de $1 à $2 sur $3.',
    selector: 'main, [role="status"], [aria-live], [class*="pagination"], [class*="results"]',
    apps: ['catalog']
  },
  {
    pattern: /^Account menu for\s+(.+)$/i,
    replacement: 'Menu du compte de $1',
    selector: 'header, button, [role="banner"]',
    apps: ['catalog', 'learner-dashboard', 'learning', 'authoring']
  },
  {
    pattern: /^Page\s+(\d+),\s+Current Page,\s+of\s+(\d+)$/,
    replacement: 'Page $1, page actuelle, sur $2',
    selector: 'main, nav, [role="navigation"], [aria-label]',
    apps: ['catalog']
  },
  {
    pattern: /^Starts:\s*(.+)$/,
    replacement: 'Début : $1',
    selector: '.course-card, [class*="course-card"], [class*="course"]',
    apps: ['catalog']
  }
]);

const normalizeGeekGuruUiMessage = value => `${value || ''}`.replace(/\s+/g, ' ').trim();

const localizeGeekGuruDocumentTitle = () => {
  if (typeof document === 'undefined' || geekGuruLanguage !== 'fr') return;
  const replacements = {
    learning: [/^Course(?=\s*\|)/, 'Cours'],
    catalog: [/^Courses(?=\s*\|)/, 'Formations'],
    authoring: [/^Course Authoring(?=\s*\|)/, 'Création de cours']
  };
  const replacement = replacements[geekGuruAppId];
  if (!replacement || !replacement[0].test(document.title)) return;
  document.title = document.title.replace(replacement[0], replacement[1]);
};

const geekGuruUiContextMatches = (element, source) => {
  const selector = geekGuruFrenchUiContexts[source];
  return (
    geekGuruMessageAllowedForApp(source)
    && geekGuruIsChromeUi(element)
    && (!selector || Boolean(element?.closest(selector)))
  );
};

const translateGeekGuruUiMessage = (source, element) => {
  const exact = geekGuruFrenchUiMessages[source];
  if (exact && geekGuruUiContextMatches(element, source)) return exact;
  const dynamicMessage = geekGuruFrenchUiPatterns.find(
    ({ pattern, selector, apps }) => (
      apps.includes(geekGuruAppId)
      && geekGuruIsChromeUi(element)
      && pattern.test(source)
      && (!selector || element?.closest(selector))
    ),
  );
  return dynamicMessage ? source.replace(dynamicMessage.pattern, dynamicMessage.replacement) : null;
};

const localizeGeekGuruAttribute = (element, attributeName) => {
  const original = element.getAttribute(attributeName);
  const source = normalizeGeekGuruUiMessage(original);
  const translated = translateGeekGuruUiMessage(source, element);
  if (translated && translated !== source) {
    element.setAttribute(attributeName, translated);
    element.dataset.geekguruI18n = 'fr';
    return;
  }

  // Learning exposes composite accessible names such as
  // "Bienvenue…, Completed section Close". Replace only these bounded status
  // fragments in aria/title attributes; never run this substitution on visible
  // or course-authored text.
  if (
    ['aria-label', 'title'].includes(attributeName)
    && geekGuruAppId === 'learning'
    && geekGuruIsChromeUi(element)
    && /\b(?:Completed|Incomplete) section\b/.test(source)
  ) {
    const translatedState = source
      .replace(/\bIncomplete section\b/g, 'Section non terminée')
      .replace(/\bCompleted section\b/g, 'Section terminée')
      .replace(/\bClose\b/g, 'Fermer')
      .replace(/\bOpen\b/g, 'Ouvrir');
    element.setAttribute(attributeName, translatedState);
    element.dataset.geekguruI18n = 'fr';
    return;
  }

  // Accessible names may append the course title to the action label. Translate
  // the reviewed prefix without discarding the contextual part of the label.
  const prefixes = [
    ['Resume course', 'Reprendre le cours'],
    ['Enroll now', "S'inscrire"],
    ['Completed section', 'Section terminée'],
    ['Incomplete section', 'Section non terminée']
  ];
  prefixes.some(([english, french]) => {
    if (!source.startsWith(`${english} `) && !source.startsWith(`${english}:`)) return false;
    if (!geekGuruUiContextMatches(element, english)) return false;
    element.setAttribute(attributeName, `${french}${source.slice(english.length)}`);
    element.dataset.geekguruI18n = 'fr';
    return true;
  });
};

const localizeGeekGuruTextNode = (node) => {
  const parent = node.parentElement;
  if (
    !parent
    || geekGuruIsExcludedUi(parent)
    || parent.closest('script, style, code, pre, textarea, [contenteditable="true"]')
  ) return;
  const source = normalizeGeekGuruUiMessage(node.nodeValue);
  const translated = translateGeekGuruUiMessage(source, parent);
  if (!translated || translated === source) return;
  const leadingWhitespace = node.nodeValue.match(/^\s*/)?.[0] || '';
  const trailingWhitespace = node.nodeValue.match(/\s*$/)?.[0] || '';
  node.nodeValue = `${leadingWhitespace}${translated}${trailingWhitespace}`;
  parent.dataset.geekguruI18n = 'fr';
};

const localizeGeekGuruMfe = (root = document) => {
  if (
    typeof document === 'undefined'
    || geekGuruLanguage !== 'fr'
    || !geekGuruSupportedMfeApps.includes(geekGuruAppId)
    || !root
    || (root.nodeType === Node.ELEMENT_NODE && geekGuruIsExcludedUi(root))
  ) return;
  const visit = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      localizeGeekGuruTextNode(node);
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      ['aria-label', 'title', 'placeholder'].forEach(
        attributeName => localizeGeekGuruAttribute(node, attributeName),
      );
    }
  };
  visit(root);
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        return geekGuruIsExcludedUi(element) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      },
    },
  );
  while (walker.nextNode()) visit(walker.currentNode);
};

if (typeof document !== 'undefined') {
  document.documentElement.dataset.geekguruRelease = '3.1.0';

  const criticalStyleId = 'geekguru-mfe-shell-critical-v310';
  if (!document.getElementById(criticalStyleId)) {
    const criticalStyle = document.createElement('style');
    criticalStyle.id = criticalStyleId;
    criticalStyle.textContent = `
      :root{--gg-nav:#071827;--gg-brand-blue:#1677d2;--gg-primary:#238636;--gg-primary-strong:#116329;--gg-border:#d5dee7;--gg-text:#111827;--gg-muted:#536170;--gg-font:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif}
      html[data-paragon-theme-variant="dark"]{--gg-primary-strong:#7ee787;--gg-text:#f4f8fb}
      .gg-academy-brand,.gg-academy-brand__home,.gg-academy-footer__brand{display:flex;align-items:center;min-width:0}
      .gg-academy-brand{gap:.65rem}.gg-academy-brand__home{gap:.55rem;min-inline-size:44px;min-height:44px;color:var(--gg-text)!important;text-decoration:none!important}
      .gg-academy-mark{display:grid;width:40px;height:40px;flex:0 0 40px;place-items:center}.gg-academy-mark svg{display:block;width:100%;height:100%}
      .gg-academy-mark__frame{fill:var(--gg-nav)}.gg-academy-mark__accent{fill:var(--gg-brand-blue);stroke:#fff;stroke-width:1.2}.gg-academy-mark text{fill:#fff;font:900 12px/1 var(--gg-font)}.gg-academy-mark circle{fill:var(--gg-primary);stroke:#fff;stroke-width:1.2}
      .gg-academy-brand__text{display:flex;min-width:0;flex-direction:column;line-height:1.08;white-space:nowrap}.gg-academy-brand__text strong{font:800 .95rem/1.1 var(--gg-font)}.gg-academy-brand__text small{margin-top:.2rem;color:var(--gg-muted);font:700 .68rem/1.1 var(--gg-font)}
      .gg-academy-brand__site{display:inline-flex;min-inline-size:44px;min-height:44px;align-items:center;padding:.45rem .65rem;border:1px solid var(--gg-border);border-radius:.5rem;font:700 .75rem/1 var(--gg-font);white-space:nowrap;text-decoration:none!important}
      .gg-academy-mobile-brand{display:inline-flex;min-inline-size:44px;min-height:44px;align-items:center;color:var(--gg-text)!important}
      body:has(.gg-academy-mobile-brand) a.logo:has(>img.logo-image){display:none!important}
      .learning-header .gg-academy-brand__site,.learning-header .gg-academy-brand__text small{display:none!important}
      .gg-academy-footer{display:block;width:100%;margin-top:auto;padding:2rem 1rem 1.25rem;border-top:1px solid #29445d;background:#071827;color:#dbe7f0;font-family:var(--gg-font)}
      .gg-academy-footer__inner,.gg-academy-footer__meta{width:100%;max-width:1180px;margin:0 auto}.gg-academy-footer__inner{display:flex;justify-content:space-between;align-items:flex-start;gap:2rem}.gg-academy-footer__brand{gap:.65rem;color:#fff}.gg-academy-footer__brand strong,.gg-academy-footer__brand small{display:block}.gg-academy-footer__brand small{margin-top:.2rem;color:#a9b8c7;font-size:.75rem}
      .gg-academy-footer nav{display:grid;grid-template-columns:repeat(4,max-content);justify-content:end;gap:.75rem 1.2rem}.gg-academy-footer a{display:inline-flex;min-inline-size:44px;min-height:44px;align-items:center;color:#e9f2f8!important;font-size:.83rem;font-weight:650;text-decoration:none!important}.gg-academy-footer__meta{display:flex;justify-content:space-between;gap:1rem;margin-top:1.25rem;padding-top:1rem;border-top:1px solid #29445d;color:#9fb0bf;font-size:.76rem}
      .gg-catalog-hero{width:min(100%,920px);margin-inline:auto;text-align:center}.gg-catalog-hero__eyebrow{display:inline-block;margin-bottom:.9rem;color:var(--gg-primary-strong);font-size:.75rem;font-weight:850;letter-spacing:.12em;text-transform:uppercase}.gg-catalog-hero__title{max-width:18ch;margin:0 auto;color:var(--gg-text)!important;font-size:clamp(2.25rem,5vw,4.25rem);line-height:1.04;letter-spacing:-.045em}.gg-catalog-hero__subtitle{max-width:66ch;margin:1rem auto 0!important;color:var(--gg-muted)!important;font-size:clamp(1rem,1.7vw,1.25rem);line-height:1.6}
      @media(max-width:991px){.gg-academy-footer__inner{flex-direction:column}.gg-academy-footer nav{grid-template-columns:repeat(3,max-content);justify-content:start}}
      @media(max-width:575px){.gg-academy-brand__site,.gg-academy-brand__text small{display:none!important}.gg-academy-footer nav{width:100%;grid-template-columns:repeat(2,minmax(0,1fr))}.gg-academy-footer__meta{flex-direction:column}.gg-catalog-hero__title{font-size:clamp(2rem,10vw,2.8rem)}.gg-catalog-hero__subtitle{font-size:1rem;line-height:1.5}}
    `;
    document.head.appendChild(criticalStyle);
  }

  const commonStyleId = 'geekguru-mfe-shell-v310';
  if (geekGuruAssetBase && !document.getElementById(commonStyleId)) {
    const commonStyle = document.createElement('link');
    commonStyle.id = commonStyleId;
    commonStyle.rel = 'stylesheet';
    commonStyle.href = `${geekGuruAssetBase}/static/indigo/css/geekguru-mfe-common-v310.css?v=${encodeURIComponent(geekGuruAssetVersion)}`;
    commonStyle.onload = () => {
      document.documentElement.dataset.geekguruShell = '3.1.0';
    };
    document.head.appendChild(commonStyle);
  }
}

Object.entries(config.pluginSlots || {}).forEach(([slotId, slot]) => {
  (slot.plugins || []).forEach((plugin) => {
    if (plugin.widget?.id === 'custom_logo') {
      plugin.widget.RenderWidget = GeekGuruLogo;
    }
    if (plugin.widget?.id === 'indigo_footer') {
      plugin.widget.RenderWidget = GeekGuruFooter;
    }
  });
});

// The Catalog MFE exposes the home-page overlay as an official frontend plugin
// slot. Replace only that default widget; the upstream search, course list and
// promotional-video slots remain intact.
if (process.env.APP_ID === 'catalog') {
  const geekGuruCatalogMetadataScriptId = 'geekguru-catalog-metadata-v310';
  if (
    typeof document !== 'undefined'
    && geekGuruAssetBase
    && !document.getElementById(geekGuruCatalogMetadataScriptId)
  ) {
    const metadataScript = document.createElement('script');
    metadataScript.id = geekGuruCatalogMetadataScriptId;
    metadataScript.defer = true;
    metadataScript.src = `${geekGuruAssetBase}/static/indigo/js/geekguru-catalog-metadata-v310.js?v=${encodeURIComponent(geekGuruAssetVersion)}`;
    document.head.appendChild(metadataScript);
  }

  const geekGuruCatalogOverlaySlotId = 'org.openedx.frontend.catalog.home_page.overlay_html';
  const geekGuruCatalogOverlaySlot = config.pluginSlots[geekGuruCatalogOverlaySlotId] || {
    keepDefault: true,
    plugins: [],
  };
  geekGuruCatalogOverlaySlot.plugins = (geekGuruCatalogOverlaySlot.plugins || []).filter(
    plugin => plugin.widget?.id !== 'geekguru_catalog_home_overlay'
      && !(plugin.op === PLUGIN_OPERATIONS.Hide && plugin.widgetId === 'default_contents'),
  );
  geekGuruCatalogOverlaySlot.plugins.unshift({
    op: PLUGIN_OPERATIONS.Hide,
    widgetId: 'default_contents',
  });
  geekGuruCatalogOverlaySlot.plugins.push({
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'geekguru_catalog_home_overlay',
      type: DIRECT_PLUGIN,
      priority: 1,
      RenderWidget: GeekGuruCatalogHero,
    },
  });
  config.pluginSlots[geekGuruCatalogOverlaySlotId] = geekGuruCatalogOverlaySlot;
}

// Keep Indigo's theme switch and account controls intact. Insert the compact
// Academy link as its own mobile-header widget instead of hijacking another one.
const geekGuruMobileSlot = config.pluginSlots?.mobile_header_slot;
if (geekGuruMobileSlot) {
  geekGuruMobileSlot.plugins = (geekGuruMobileSlot.plugins || []).filter(
    plugin => plugin.widget?.id !== 'geekguru_mobile_brand',
  );
  geekGuruMobileSlot.plugins.unshift({
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'geekguru_mobile_brand',
      type: DIRECT_PLUGIN,
      priority: 1,
      RenderWidget: GeekGuruMobileLogo,
    },
  });
}

// Authoring can keep Indigo's native image logo and therefore never mount the
// locale bridge from GeekGuruLogo. Add the bridge to a stable Studio slot so
// localization does not depend on the header implementation.
if (geekGuruAppId === 'authoring') {
  const authoringLocaleSlotId = 'org.openedx.frontend.layout.studio_header_search_button_slot.v1';
  const authoringLocaleSlot = config.pluginSlots?.[authoringLocaleSlotId] || {
    keepDefault: true,
    plugins: [],
  };
  authoringLocaleSlot.plugins = (authoringLocaleSlot.plugins || []).filter(
    plugin => plugin.widget?.id !== 'geekguru_locale_bridge',
  );
  authoringLocaleSlot.plugins.push({
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'geekguru_locale_bridge',
      type: DIRECT_PLUGIN,
      priority: 1,
      RenderWidget: GeekGuruLocaleBridge,
    },
  });
  config.pluginSlots[authoringLocaleSlotId] = authoringLocaleSlot;
}

// Indigo does not pre-create the same footer slot for every MFE. Create the
// applicable slot, hide the upstream default and insert exactly one Academy
// footer while preserving unrelated helpers such as the theme-cookie reader.
const upsertGeekGuruFooter = (slotId) => {
  const slot = config.pluginSlots?.[slotId] || { keepDefault: false, plugins: [] };
  slot.keepDefault = false;
  slot.plugins = (slot.plugins || []).filter(
    plugin => !['geekguru_footer', 'indigo_footer'].includes(plugin.widget?.id)
      && !(plugin.op === PLUGIN_OPERATIONS.Hide && plugin.widgetId === 'default_contents'),
  );
  slot.plugins.unshift({
    op: PLUGIN_OPERATIONS.Hide,
    widgetId: 'default_contents',
  });
  slot.plugins.push({
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'geekguru_footer',
      type: DIRECT_PLUGIN,
      priority: 1,
      RenderWidget: GeekGuruFooter,
    },
  });
  config.pluginSlots[slotId] = slot;
};

upsertGeekGuruFooter('org.openedx.frontend.layout.footer.v1');
if (geekGuruAppId === 'authoring') {
  upsertGeekGuruFooter('org.openedx.frontend.layout.studio_footer.v1');
}

// Ulmo's French learner-dashboard catalogue currently translates the action
// message "Resume" as "CV" and renders it as href="#". Preserve the upstream
// card behaviour while making the action explicit, copyable and accessible.
const repairGeekGuruCourseActions = (root = document) => {
  if (
    typeof document === 'undefined'
    || geekGuruAppId !== 'learner-dashboard'
    || !['fr', 'en'].includes(geekGuruLanguage)
    || !root
  ) return;
  const cards = new Set();
  if (root.matches?.('.course-card')) cards.add(root);
  root.querySelectorAll?.('.course-card').forEach(card => cards.add(card));
  cards.forEach((card) => {
    const learningLink = card.querySelector('a[href*="/learning/course/"]');
    const action = Array.from(card.querySelectorAll('a.btn-primary')).find(
      link => link.getAttribute('href') === '#' || ['CV', 'Resume'].includes(link.textContent.trim()),
    );
    if (!learningLink || !action) return;
    const resumeLabel = geekGuruLanguage === 'fr' ? 'Reprendre le cours' : 'Resume course';
    action.href = learningLink.href;
    action.textContent = resumeLabel;
    action.removeAttribute('role');
    const title = card.querySelector('.course-card-title, h3')?.textContent?.trim();
    action.setAttribute('aria-label', title ? `${resumeLabel} ${title}` : resumeLabel);
    action.dataset.geekguruCourseAction = 'fixed';
  });
};

if (
  typeof document !== 'undefined'
  && geekGuruSupportedMfeApps.includes(geekGuruAppId)
) {
  syncGeekGuruEffectiveLanguage(document);
  if (geekGuruAppId === 'learner-dashboard' && geekGuruLanguage) {
    repairGeekGuruCourseActions(document);
  }
  if (geekGuruLanguage === 'fr') {
    localizeGeekGuruMfe(document);
    localizeGeekGuruDocumentTitle();
  }
  let geekGuruRepairQueued = false;
  const geekGuruMutationRoots = new Set();
  const queueGeekGuruMutationRoot = (node) => {
    const root = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    if (!root || geekGuruIsExcludedUi(root)) return;
    for (const queuedRoot of geekGuruMutationRoots) {
      if (queuedRoot === root || queuedRoot.contains?.(root)) return;
      if (root.contains?.(queuedRoot)) geekGuruMutationRoots.delete(queuedRoot);
    }
    geekGuruMutationRoots.add(root);
  };
  const geekGuruObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => queueGeekGuruMutationRoot(node));
      } else {
        queueGeekGuruMutationRoot(mutation.target);
      }
    });
    if (!geekGuruMutationRoots.size || geekGuruRepairQueued) return;
    geekGuruRepairQueued = true;
    window.requestAnimationFrame(() => {
      geekGuruRepairQueued = false;
      const localeChanged = syncGeekGuruEffectiveLanguage(document);
      if (localeChanged) {
        if (geekGuruAppId === 'learner-dashboard') repairGeekGuruCourseActions(document);
        if (geekGuruLanguage === 'fr') {
          localizeGeekGuruMfe(document);
          localizeGeekGuruDocumentTitle();
        }
      } else {
        geekGuruMutationRoots.forEach((root) => {
          if (geekGuruAppId === 'learner-dashboard') repairGeekGuruCourseActions(root);
          if (geekGuruLanguage === 'fr') localizeGeekGuruMfe(root);
        });
        localizeGeekGuruDocumentTitle();
      }
      geekGuruMutationRoots.clear();
    });
  });
  geekGuruObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['aria-label', 'title', 'placeholder', 'data-geekguru-intl-locale'],
    characterData: true,
    childList: true,
    subtree: true,
  });
}
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;